import { useEffect } from 'react';

import { useTerminalAgent } from '@tradeparadigm/terminal-react';

import { useBlockTerminalPendingStore } from '#/features/terminal/pending-message-store';

/**
 * Drains a queued message (e.g. from "Analyze in Terminal") once the agent
 * reaches `authenticated`. Pushed by other surfaces via the pending-message
 * store; this component is the only consumer.
 *
 * The queued item carries optional hidden `context` (the `/analyze`d trade's
 * structured data). It's passed to `sendMessage(text, { context })` so it
 * rides in the gateway payload but stays out of the chat bubble — the same
 * hidden channel `systemPreamble` uses. This is how the agent actually gets
 * the trade data, since the gateway doesn't implement `set_block_trade_context`.
 *
 * Rendered through `<Terminal agentEffects>` so it can reach the agent via
 * `useTerminalAgent()` while living entirely in host code. Renders nothing.
 */
export default function PendingMessageDrain() {
  const { status, sendMessage } = useTerminalAgent();
  const pending = useBlockTerminalPendingStore((s) => s.pending);
  const consumePending = useBlockTerminalPendingStore((s) => s.consumePending);

  useEffect(() => {
    if (pending == null) return;
    if (status !== 'authenticated') return;
    const drained = consumePending();
    if (drained != null) {
      sendMessage(drained.text, { context: drained.context });
    }
  }, [pending, status, consumePending, sendMessage]);

  return null;
}
